import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setAdminData, setUserStoreRight, removeUserStoreRight } from '../store/adminSlice';
import GravityLogo from './GravityLogo';
import { PW_Users_API, PW_Stores_API, PW_UserStoreRight_Save_API } from '../Server/ApiV2';
import Spinner from './Spinner';

export default function UserStoreRights() {
  const dispatch = useDispatch();
  const { users, stores, userStoreRights, userTypes } = useSelector(s => s.admin);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [usersRes, storesRes] = await Promise.all([PW_Users_API(), PW_Stores_API()]);
      if (cancelled) return;
      const payload = {};
      if (storesRes?.data?.success) payload.stores = storesRes.data.data;
      if (usersRes?.data?.success) {
        const u = usersRes.data.data;
        payload.users = u.users;
        payload.userStoreRights = u.userStoreRights;
        payload.userTypes = u.userTypes;
      }
      if (Object.keys(payload).length) dispatch(setAdminData(payload));
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);
  const [selUser, setSelUser] = useState('');

  const activeUsers = users.filter(u => u.status === 'Active');
  const activeStores = stores.filter(s => s.status === 'Active');
  const selectedUser = users.find(u => u.id === selUser);
  const userRights = userStoreRights.filter(r => r.userId === selUser);

  const getRight = (storeId) => userRights.find(r => r.storeId === storeId);
  const getUserTypeName = (id) => userTypes.find(t => t.id === id)?.name || id;

  const saveRight = (rightData) => {
    PW_UserStoreRight_Save_API(rightData).catch(err => console.error('[Rights] Save failed:', err));
  };

  const handleToggle = (storeId, field) => {
    const existing = getRight(storeId);
    if (existing) {
      const updated = { ...existing, userId: selUser, storeId, [field]: !existing[field] };
      dispatch(setUserStoreRight({ userId: selUser, storeId, [field]: !existing[field] }));
      saveRight(updated);
    } else {
      const newRight = { userId: selUser, storeId, canView: false, canGenerate: false, canPrint: false, canManage: false, [field]: true };
      dispatch(setUserStoreRight({ userId: selUser, storeId, [field]: true }));
      saveRight(newRight);
    }
  };

  const handleToggleAll = (storeId) => {
    const existing = getRight(storeId);
    if (existing) {
      dispatch(removeUserStoreRight({ userId: selUser, storeId }));
      saveRight({ userId: selUser, storeId, canView: false, canGenerate: false, canPrint: false, canManage: false });
    } else {
      const allRights = { userId: selUser, storeId, canView: true, canGenerate: true, canPrint: true, canManage: true };
      dispatch(setUserStoreRight(allRights));
      saveRight(allRights);
    }
  };

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>User & Store Rights</h2>
          <div className="page-header-sub">Configure user access permissions for each store</div>
        </div>
      </div>

      <div className="card mb-24">
        <div className="card-body" style={{ padding: '12px 18px' }}>
          <div className="form-inline" style={{ flexWrap: 'wrap', gap: 16 }}>
            <div className="form-inline">
              <label className="form-label" style={{ marginBottom: 0 }}>User:</label>
              <select className="form-select" style={{ width: 280 }}
                value={selUser} onChange={e => setSelUser(e.target.value)}>
                <option value="">Select a user...</option>
                {activeUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({getUserTypeName(u.userTypeId)})</option>
                ))}
              </select>
            </div>
            {selectedUser && (
              <div style={{ display: 'flex', gap: 8 }}>
                <span className="chip chip-purple">{getUserTypeName(selectedUser.userTypeId)}</span>
                <span className="chip chip-green">{userRights.length} stores assigned</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {selUser ? (
        <div className="card">
          <div className="card-title gold">
            Rights for {selectedUser?.name}
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div className="table-wrap">
              <table className="rights-matrix">
                <thead>
                  <tr>
                    <th>Store</th>
                    <th>Code</th>
                    <th>City</th>
                    <th>Assigned</th>
                    <th>View</th>
                    <th>Generate</th>
                    <th>Print</th>
                    <th>Manage</th>
                  </tr>
                </thead>
                <tbody>
                  {activeStores.map(store => {
                    const right = getRight(store.id);
                    const isAssigned = !!right;
                    return (
                      <tr key={store.id} style={{ background: isAssigned ? '#f9f8fc' : '' }}>
                        <td>{store.name}</td>
                        <td><span className="chip chip-blue">{store.code}</span></td>
                        <td className="text-sm text-muted">{store.city}</td>
                        <td>
                          <input type="checkbox" checked={isAssigned}
                            onChange={() => handleToggleAll(store.id)} />
                        </td>
                        <td>
                          <input type="checkbox" checked={right?.canView || false}
                            disabled={!isAssigned}
                            onChange={() => handleToggle(store.id, 'canView')} />
                        </td>
                        <td>
                          <input type="checkbox" checked={right?.canGenerate || false}
                            disabled={!isAssigned}
                            onChange={() => handleToggle(store.id, 'canGenerate')} />
                        </td>
                        <td>
                          <input type="checkbox" checked={right?.canPrint || false}
                            disabled={!isAssigned}
                            onChange={() => handleToggle(store.id, 'canPrint')} />
                        </td>
                        <td>
                          <input type="checkbox" checked={right?.canManage || false}
                            disabled={!isAssigned}
                            onChange={() => handleToggle(store.id, 'canManage')} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <GravityLogo size={48} circle />
              <p>Select a user above to manage their store access rights</p>
            </div>
          </div>
        </div>
      )}

      {selUser && (
        <div className="card mt-24">
          <div className="card-title">Rights Summary</div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {userRights.map(r => {
                const store = stores.find(s => s.id === r.storeId);
                if (!store) return null;
                const perms = [
                  r.canView && 'View',
                  r.canGenerate && 'Generate',
                  r.canPrint && 'Print',
                  r.canManage && 'Manage',
                ].filter(Boolean);
                return (
                  <div key={r.storeId} style={{
                    padding: '8px 14px', borderRadius: 8,
                    background: '#f9f8fc', border: '1px solid #e0dce8',
                    fontSize: 12,
                  }}>
                    <div className="fw-600" style={{ color: '#2d1b69', marginBottom: 4 }}>{store.name}</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {perms.map(p => (
                        <span key={p} className="chip chip-green">{p}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
              {userRights.length === 0 && (
                <span className="text-muted">No stores assigned</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
